import { ChangeEvent, Fragment, ReactElement, useEffect, useState } from "react";
import { IDelay, IDelayEvents, IDelaysResult, IPanelProps } from "./types";
import { formatTimeWithSeconds } from "../../../../system/utils";
import RowComponent from "../../components/rowComponent";
import IconButton from "@mui/material/IconButton";
import CachedIcon from "@mui/icons-material/Cached";
import { Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs } from "@mui/material";
import { useAsyncFetch } from "../../../../system/useFetch";
import { IColumn, IShiftHistory, IState } from "../../../../types";
import StyledTableRow from "../../../../components/styledTableRow";
import { Duration } from "luxon";
import { useSelector } from "react-redux";

const a11yProps = (index: number): any => {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
};

const columns: IColumn[] = [
  {
    id: "beginDatetime",
    label: "Start Time",
    minWidth: 80
  },
  {
    id: "duration",
    label: "Duration ↓",
    minWidth: 90,
  },
  {
    id: "responsibility",
    label: "Responsibility",
    minWidth: 100,
  },
  {
    id: "equipment",
    label: "Equipment",
    minWidth: 100,
  },
  {
    id: "observation",
    label: "Observation",
    minWidth: 100,
  },
  {
    id: "comment",
    label: "Notes",
    minWidth: 100,
  },
];

const eventColumns: IColumn[] = [
  {
    id: "count",
    label: "Count",
    minWidth: 80
  },
  {
    id: "duration",
    label: "Duration ↓",
    minWidth: 80,
  },
  {
    id: "equipment",
    label: "Responsibility\\Area\\Equipment",
    minWidth: 100,
  }
];


interface IProps extends IPanelProps<IDelaysResult> {
  shiftHistory: IShiftHistory;
}

const DelaysPanel = ({ shiftHistory, property, onChange }: IProps): ReactElement => {
  const darkMode = useSelector((state: IState) => state.darkMode);
  const [tabSelected, setTabSelected] = useState(0);
  const [ dispatchAPI, { data } ] = useAsyncFetch<[IDelay[], IDelayEvents[]]>();

  const handleChange = (_event: ChangeEvent<any>, newValue: number) => {
    setTabSelected(newValue);
  };

  const onRefreshDelays = () => {
    dispatchAPI([{
      module: "delay",
      query: "getTopDelayBaseByShift",
      payload: {
        shiftHistoryID: shiftHistory.shiftHistoryID,
        filter: ["Downday", "Stnd P. Off"]
      }
    }, {
      module: "delay",
      query: "getTopEventsByShift",
      payload: {
        shiftHistoryID: shiftHistory.shiftHistoryID,
        filter: ["Downday", "Stnd P. Off"]
      }
    }]).then(result => {
      if (result) {
        onChange(property, { list: result[0], events: result[1] });
      } else {
        onChange(property, { list: [], events: [] });
      }
    });
  };

  useEffect(() => {
    onRefreshDelays();
  }, []);

  return (
    <div className="display-flex-column gap-1 p-1">
      <div className="display-flex-row">
        <div className="flex-grow-1">
          <RowComponent title="Update" ratio={.2}>
            <div>
              <IconButton size={"small"} color="info" onClick={onRefreshDelays}>
                <CachedIcon/>
              </IconButton>
            </div>
          </RowComponent>
        </div>

        <div className="flex-grow-1"/>

        <Tabs
          value={tabSelected}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          style={{ background: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)" }}>
          <Tab label="Events" {...a11yProps(0)} />
          <Tab label="Top 10" {...a11yProps(1)} />
        </Tabs>
      </div>

      { tabSelected === 0
        ? <Table stickyHeader={true}  size={"small"}>
          <TableHead>
            <TableRow>
              {eventColumns.map((column) => {
                return (
                  <TableCell
                    key={`${column.id}`}
                    style={{ backgroundColor: "#b0bec5",  minWidth: column.minWidth }}>
                    {column.id !== "actions" ? column.label : <Fragment /> }
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            { data && (data[1] ?? []).map((row, index) => {
              const duration = Duration.fromMillis(row.duration * 1000).toFormat("hh:mm:ss");
              let text = row.equipment ? `\\${row.equipment}` : null;

              if (row.area) {
                text = `\\${row.area}${text ?? ""}`;
              }

              text = text ? `${row.responsibility}${text}` : `${row.responsibility}`;

              return (
                <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`event-row-${index}`} >
                  <TableCell>
                    {row.count}
                  </TableCell>
                  <TableCell>
                    {duration}
                  </TableCell>
                  <TableCell>
                    {text}
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>

        : <Table stickyHeader={true}  size={"small"} style={{ marginTop: ".5em" }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                return (
                  <TableCell
                    key={`${column.id}`}
                    style={{ backgroundColor: "#b0bec5", minWidth: column.minWidth }}>
                    {column.id !== "actions" ? column.label : <Fragment /> }
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            { data && (data[0] ?? []).map(row => {
              const duration = Duration.fromMillis((row.duration ?? 0) * 1000).toFormat("hh:mm:ss");

              return (
                <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`delay-row-${row.delayID}`} >
                  <TableCell>
                    {formatTimeWithSeconds(row.beginDatetime)}
                  </TableCell>
                  <TableCell>
                    {duration}
                  </TableCell>
                  <TableCell>
                    {row.responsibility}
                  </TableCell>
                  <TableCell>
                    {row.equipment}
                  </TableCell>
                  <TableCell>
                    {row.observation}
                  </TableCell>
                  <TableCell>
                    {row.comment}
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      }
    </div>
  );
};

export default DelaysPanel;